import { useState } from "react";
import axios from "axios";
import { Lock, Package, RefreshCw, Download } from "lucide-react";
import { money } from "@/components/store/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/orders`, { params: { key } });
      setOrders(data);
    } catch (err) {
      setError(err?.response?.status === 401 ? "Invalid admin key." : "Failed to load orders.");
      setOrders(null);
    } finally {
      setLoading(false);
    }
  };

  const revenue = orders ? orders.reduce((s, o) => s + o.total, 0) : 0;

  const STATUSES = ["New", "Paid", "Shipped", "Delivered", "Cancelled"];
  const STATUS_COLOR = {
    New: "#B8C5C4",
    Paid: "#D4AF37",
    Shipped: "#6FB6FF",
    Delivered: "#63A375",
    Cancelled: "#E06B5E",
  };

  const changeStatus = async (id, status) => {
    const order = orders.find((o) => o.id === id);
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    try {
      await axios.patch(`${API}/orders/${id}/status`, { status, tracking: order?.tracking || "" }, { params: { key } });
    } catch (err) {
      setError("Failed to update status.");
    }
  };

  const setTracking = (id, tracking) => {
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, tracking } : o)));
  };

  const saveTracking = async (id) => {
    const order = orders.find((o) => o.id === id);
    if (!order) return;
    try {
      await axios.patch(`${API}/orders/${id}/status`, { status: order.status || "New", tracking: order.tracking || "" }, { params: { key } });
    } catch (err) {
      setError("Failed to save tracking.");
    }
  };

  const downloadCSV = () => {
    const cols = ["id", "created_at", "full_name", "email", "location", "quantity", "subtotal", "discount", "shipping", "total", "status", "tracking", "ip_address", "software_used"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = orders.map((o) => cols.map((c) => esc(o[c])).join(","));
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ubiquinol-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#052221] text-[#F4F1EB] px-5 sm:px-8 py-14">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Package className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
          <h1 className="font-display text-3xl">Orders Dashboard</h1>
        </div>
        <p className="text-[#B8C5C4] text-sm mb-10">Captured order records — email, location, IP address & software used.</p>

        {!orders ? (
          <form onSubmit={load} className="max-w-md bg-[#08302D] border border-white/10 rounded-sm p-7">
            <label className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B8C5C4] mb-3">
              <Lock className="w-4 h-4 text-[#D4AF37]" /> Admin Key
            </label>
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Enter admin key"
              className="w-full bg-[#0C3F3B] border border-white/10 px-4 py-3 text-[#F4F1EB] rounded-sm mb-4"
            />
            {error && <p data-testid="admin-error" className="text-[#E06B5E] text-sm mb-4">{error}</p>}
            <button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-[#052221] py-3 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#F0C851] transition-colors disabled:opacity-40">
              {loading ? "Loading…" : "View Orders"}
            </button>
          </form>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden mb-8">
              {[["Orders", orders.length], ["Bottles Sold", orders.reduce((s, o) => s + o.quantity, 0)], ["Revenue", `${money(revenue)} CAD`]].map(([k, v]) => (
                <div key={k} className="bg-[#08302D] p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#B8C5C4] mb-2">{k}</p>
                  <p className="font-display text-2xl text-[#F4F1EB]">{v}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mb-5">
              <button data-testid="admin-refresh-btn" onClick={load} className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#D4AF37] hover:text-[#F0C851]">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <button data-testid="export-csv-btn" onClick={downloadCSV} disabled={orders.length === 0} className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#052221] bg-[#D4AF37] px-3 py-2 hover:bg-[#F0C851] transition-colors disabled:opacity-40">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
            <div className="overflow-x-auto border border-white/10 rounded-sm">
              <table data-testid="orders-table" className="w-full text-sm">
                <thead className="bg-[#08302D] text-[#B8C5C4] text-xs uppercase tracking-[0.15em]">
                  <tr>
                    {["Date", "Name", "Email", "Location", "Qty", "Total", "Status", "Tracking", "IP Address", "Software"].map((h) => (
                      <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid="order-row" className="border-t border-white/10 align-top">
                      <td className="px-4 py-3 whitespace-nowrap text-[#B8C5C4]">{new Date(o.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3">{o.full_name}</td>
                      <td className="px-4 py-3">{o.email}</td>
                      <td className="px-4 py-3 max-w-[220px]">{o.location}</td>
                      <td className="px-4 py-3">{o.quantity}</td>
                      <td className="px-4 py-3 text-[#D4AF37]">{money(o.total)}</td>
                      <td className="px-4 py-3">
                        <select
                          data-testid="order-status-select"
                          value={o.status || "New"}
                          onChange={(e) => changeStatus(o.id, e.target.value)}
                          style={{ color: STATUS_COLOR[o.status || "New"] }}
                          className="bg-[#0C3F3B] border border-white/15 rounded-sm px-2 py-1.5 text-xs focus:border-[#D4AF37] cursor-pointer"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} style={{ color: "#F4F1EB" }}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          data-testid="order-tracking-input"
                          value={o.tracking || ""}
                          onChange={(e) => setTracking(o.id, e.target.value)}
                          onBlur={() => saveTracking(o.id)}
                          onKeyDown={(e) => e.key === "Enter" && saveTracking(o.id)}
                          placeholder="Tracking # / URL"
                          className="w-40 bg-[#0C3F3B] border border-white/15 rounded-sm px-2 py-1.5 text-xs text-[#F4F1EB] placeholder:text-[#B8C5C4]/50 focus:border-[#D4AF37]"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{o.ip_address}</td>
                      <td className="px-4 py-3 max-w-[260px] text-[#B8C5C4] text-xs break-words">{o.software_used}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-[#B8C5C4]">No orders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
